import { createTheme, Theme } from "@mui/material";

/**
 * Because these imports are not ts files, we cannot rewrite the import path while build
 * this leads to having absolute import paths in our published build
 * which then leads to typescript errors because the import paths cannot be resolved
 */

// eslint-disable-next-line no-relative-import-paths/no-relative-import-paths
import MarkProMedium from "../../assets/fonts/MarkOT-Medium.otf";
// eslint-disable-next-line no-relative-import-paths/no-relative-import-paths
import MarkProNarrow from "../../assets/fonts/MarkOT-Narrow.otf";
// eslint-disable-next-line no-relative-import-paths/no-relative-import-paths
import MarkProBold from "../../assets/fonts/MarkOT-Bold.otf";
// eslint-disable-next-line no-relative-import-paths/no-relative-import-paths
import EnBWDINPro from "../../assets/fonts/EnBWDINPro-Regular.ttf";

import { createPaletteTheme, SenecBasePalette } from "src/styles/senecBaseThemePalette";
import { createButtonComponentTheme } from "src/styles/ekp-theme/ekpThemeButton";
import { createTypographyTheme } from "src/styles/ekp-theme/typography";
import { breakpoints } from "src/styles/ekp-theme/breakpoints";
import { createTabComponentTheme } from "src/styles/ekp-theme/ekpThemeTab";

/**
 * Custom SENEC EKP theme for Material UI components
 * includes SENEC breakpoints, SENEC color palette, SENEC Typography
 *
 * For the customizing options refer to the official Material UI documentation:
 * https://mui.com/material-ui/customization/theming/
 */

export interface SenecEkpTheme extends Theme {
    palette: SenecBasePalette;
}

/**
 * @deprecated use colors directly via color name, e.g.: deepBlue.main
 */
export const senecPrimaryColors = {
    deepBlue: "#000099", // Tiefenblau
    impulsOrange: "#ff8f0f", // Impuls-Orange
    warmGrey: "#e5dfda", // Warmgrau
    white: "#FFFFFF", // Weiß
    nightDarkGrey: "#1C1C1C", // Nacht Dunkelgrau
    darkGrayPremium: "#3C3C3C", // Dunkelgrau Premium
    darkWarmGrey: "#707070", // Dunkel Warmgrau
    lightWarmGrey: "#F2F2F2", // Hell Warmgrau
    darkWhite: "#F9F9FA", // Dunkelweiß
};

/**
 * @deprecated use colors directly via color name, e.g.: deepBlue.main
 */
export const senecSecondaryColors = {
    limeGreen: "#84c041", // Lindgrün limegreen50
    aquamarin: "#5be3d6", // Aquamarin
    azur: "#1195eb", // Azur
    azur30: "#66C2FF", // Azur 30
    azur20: "#8fd4ff", // Azur 20
    sunYellow: "#fdc83a", // Sonnengelb
    greyBeige: "#e2c39a", // Graubeige
    tarocco: "#fc6538", // Tarocco
    warmGrey: "#c3bcb6", // Warmgrau
    neutralGrey: "#767676", //Neutralgrau
    neutralGray90: "#424242",
    neutralGray80: "#5B5B5B",
    neutralGray70: "#767676",
    neutralGray60: "#909090",
    neutralGray50: "#AAAAAA",
    neutralGray40: "#BDBDBD",
    neutralGray20: "#DCDCDC",
    neutralGray10: "#E7E7E7",
    neutralGray00: "#F2F2F2",
    neutralGray000: "#F8F8F8",
};

const baseTheme = createTheme({
    breakpoints,
});

const paletteTheme = createPaletteTheme(baseTheme);

const typographyTheme = createTypographyTheme(baseTheme, paletteTheme);

const tabComponentTheme = createTabComponentTheme(paletteTheme);

const componentsTheme = createTheme(typographyTheme, {
    components: {
        MuiCssBaseline: {
            styleOverrides: `
      @font-face {
        font-family: 'Mark Pro';
        font-style: normal;
        font-display: swap;
        font-weight: 500;   
        src: url(${MarkProMedium}) format("openType");
      }
      @font-face {
        font-family: 'Mark Pro';
        font-style: normal;
        font-display: swap;
        font-weight: 400;
        src: url(${MarkProNarrow}) format("openType");
      }
      @font-face {
        font-family: 'Mark Pro';
        font-style: normal;
        font-display: swap;
        font-weight: 800;
        src: url(${MarkProBold}) format("openType");
      }
      @font-face {
        font-family: 'EnBW DIN Pro';
        font-style: normal;
        font-display: swap;
        font-weight: 400;
        src: url(${EnBWDINPro}) format('trueType');
      }
    `,
        },
        /* Button component overrides */
        ...createButtonComponentTheme.components,
        /* Tab component overrides */
        ...tabComponentTheme.components,

        MuiTab: {
            styleOverrides: {
                root: {
                    ...(tabComponentTheme.components?.MuiTab?.styleOverrides?.root as NonNullable<unknown>),
                    [paletteTheme.breakpoints.up("xsMobile")]: {
                        fontSize: typographyTheme.typography.h4.fontSize,
                    },
                    fontWeight: typographyTheme.typography.h4.fontWeight,
                    lineHeight: typographyTheme.typography.h4.lineHeight,
                    fontFamily: typographyTheme.typography.h4.fontFamily,
                },
            },
        },

        MuiLink: {
            styleOverrides: {
                root: {
                    underline: "none",
                    textDecoration: "none",
                    color: senecSecondaryColors.neutralGrey,
                },
            },
        },
    },
});

export const senecEkpTheme = componentsTheme;
